/**
 * Dinamik onay akışları — backend `admin/approval-flows` ve `admin/approval-requests`
 * uçlarının sözleşmesi. Alan adları backend DTO'larıyla birebir aynıdır.
 */

/** Adımın onaycısı bir ROL mü yoksa tek bir KİŞİ mi. */
export enum ApprovalAssigneeType {
  ROLE = 0,
  USER = 1,
}

/**
 * Bir onay KADEMESİNİN (aynı `stepOrder`'ı paylaşan dalların) ilerleme koşulu.
 *
 * - ANY: dallardan herhangi biri onaylarsa kademe geçilir, kalanlar atlanır.
 *   "X ya da Y rolüne sahip olan onaylayabilir".
 * - ALL: her dal ayrı ayrı onaylamalıdır; dallar arasında sıra yoktur.
 *
 * Her iki politikada da tek bir RED tüm talebi reddeder.
 */
export enum ApprovalParallelPolicy {
  ANY = 0,
  ALL = 1,
}

/**
 * Adımın onaycısı hangi personelin talebini görüp onaylayabilir.
 *
 * "Her müdür yalnızca kendi personelini onaylar" kuralı REQUESTER_DEPARTMENT ile
 * kurulur. Merkezî kademe (Eğitim Yöneticisi) GLOBAL olmalıdır; kapsam
 * uygulanırsa akış son adımda kilitlenir.
 */
export enum ApprovalStepScopeType {
  /** Kapsam yok — kurum genelindeki tüm talepler. */
  GLOBAL = 0,
  /** Talep sahibi, onaycının kendi biriminin (müdürlük) alt ağacında olmalı. */
  REQUESTER_DEPARTMENT = 1,
  /** Talep sahibi, onaycının kök biriminin (daire/işletme müdürlüğü) alt ağacında olmalı. */
  REQUESTER_ROOT_DEPARTMENT = 2,
}

/** Talebin bütünsel durumu. */
export enum ApprovalRequestStatus {
  PENDING = 0,
  APPROVED = 1,
  REJECTED = 2,
  CANCELLED = 3,
}

/** Talep içindeki tek bir adımın durumu. */
export enum ApprovalStepStatus {
  PENDING = 0,
  APPROVED = 1,
  REJECTED = 2,
  SKIPPED = 3,
}

/**
 * Akışın bağlandığı kayıt türü — backend enum'unun aynadaki karşılığı.
 *
 * Motorda karşılığı olan tek tür EDUCATION_ENROLLMENT; diğerleri için kayıtlı
 * bir tüketici yok, o yüzden akış tasarım ekranı bu türü sabit gönderiyor.
 */
export enum ApprovalTargetType {
  GENERIC = "GENERIC",
  EDUCATION_ENROLLMENT = "EDUCATION_ENROLLMENT",
  EDUCATION = "EDUCATION",
  SUPPORT_TICKET = "SUPPORT_TICKET",
  LIBRARY_ITEM = "LIBRARY_ITEM",
  ANNOUNCEMENT = "ANNOUNCEMENT",
}

/** Yeni adımın mevcut bir adıma göre nereye ekleneceği. */
export enum ApprovalStepInsertPosition {
  BEFORE = "BEFORE",
  AFTER = "AFTER",
  END = "END",
}

/** Adımın atandığı rol veya kişinin görüntülenebilir özeti. */
export interface ApprovalAssignee {
  id: string;
  name: string;
}

/**
 * Tasarımdaki bir akış DALI (sunucudan gelen hali).
 *
 * Liste düz gelir: paralel bir kademe, aynı `stepOrder`'a sahip birden fazla
 * eleman olarak görünür. Ekran `stepOrder`'a göre gruplayıp `branchIndex`'e göre
 * sıralar (bkz. groupFlowSteps).
 */
export interface ApprovalFlowStep {
  id: string;
  /** KADEME numarası. Paralel dallar aynı değeri paylaşır. */
  stepOrder: number;
  /** Kademe içindeki dalın sırası. Tek dallı kademede 0. */
  branchIndex: number;
  /** Kademenin ilerleme koşulu; kademenin tüm dalları aynı değeri taşır. */
  parallelPolicy: ApprovalParallelPolicy;
  title: string;
  description: string | null;
  assigneeType: ApprovalAssigneeType;
  /** Dalın rolleri — herhangi biri onaylayabilir. Kişi dalında boş dizi. */
  roles: ApprovalAssignee[];
  /** @deprecated `roles` kullanın; kümenin ilk elemanı. */
  role: ApprovalAssignee | null;
  user: ApprovalAssignee | null;
  scopeType: ApprovalStepScopeType;
  /** Talebi görebilecek onaycı yoksa dal otomatik atlanır mı. */
  skipWhenNoApprover?: boolean;
  /**
   * Bu dalın onaycısı olabilecek AKTİF kullanıcı sayısı. 0 ise adım kimseye
   * düşmez ve akış orada durur — tasarım ekranı bunu uyarı olarak gösterir.
   * Yalnızca detay ucunda dolu gelir; listede taşınmaz.
   */
  assigneeUserCount?: number;
}

/** Akış grubu — liste ve detay için ortak gövde. */
export interface ApprovalFlow {
  id: string;
  title: string;
  description: string | null;
  targetType: string;
  isActive: boolean;
  /**
   * "Sistemin onay akışı" mı. Eğitim oluştururken akış seçilmez (Eğitim
   * Yöneticisinin bu ekrana yetkisi yok); talepler bu işaretli akıştan yürür.
   * Hedef tür başına en fazla bir akış bu işareti taşır.
   */
  isDefault: boolean;
  /** KADEME sayısı — paralel dallar tek adım sayılır. */
  stepCount: number;
  /** Liste ucunda boş dizi döner; dallar için detay ucunu kullanın. */
  steps: ApprovalFlowStep[];
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Paralel bir kademenin tek dalı (gönderim biçimi). */
export interface ApprovalFlowStepBranchInput {
  /** Gönderilmezse backend kademenin başlığını kullanır. */
  title?: string;
  description?: string;
  assigneeType: ApprovalAssigneeType;
  /** ROL dalında zorunlu; listedeki rollerden herhangi biri onaylayabilir. */
  roleIds?: string[];
  userId?: string;
  /**
   * Gönderilmezse backend kök birim kapsamı (2) kabul eder. Kapsam DAL bazlıdır:
   * paralel bir kademede bir dal kendi birimiyle, diğeri tüm kurumla sınırlı olabilir.
   */
  scopeType?: ApprovalStepScopeType;
  /** Talebi görebilecek onaycı yoksa dal otomatik atlanır. Gönderilmezse false. */
  skipWhenNoApprover?: boolean;
}

/**
 * KADEME girdisi. Sıra numarası taşınmaz — kademeler gönderildikleri dizi
 * sırasına göre 1..N olarak numaralandırılır.
 *
 * Çok dallı kademe `branches` ile gönderilir ve o zaman kademenin kendi atama
 * alanları boş bırakılır; backend ikisini birden kabul etmiyor.
 */
export interface ApprovalFlowStepInput {
  title: string;
  description?: string;
  /** Çok dallı (paralel) kademenin dalları. Tek dallı kademede gönderilmez. */
  branches?: ApprovalFlowStepBranchInput[];
  /** Gönderilmezse ANY kabul edilir. Tek dallı kademede etkisizdir. */
  parallelPolicy?: ApprovalParallelPolicy;
  /** Tek dallı kademede zorunlu; `branches` ile birlikte gönderilemez. */
  assigneeType?: ApprovalAssigneeType;
  roleIds?: string[];
  userId?: string;
  scopeType?: ApprovalStepScopeType;
  skipWhenNoApprover?: boolean;
}

export interface CreateApprovalFlowPayload {
  title: string;
  description?: string;
  targetType: ApprovalTargetType;
  isActive?: boolean;
  steps: ApprovalFlowStepInput[];
}

/** `steps` gönderilirse adım listesi tamamen değiştirilir; gönderilmezse korunur. */
export interface UpdateApprovalFlowPayload {
  title?: string;
  description?: string;
  targetType?: ApprovalTargetType;
  isActive?: boolean;
  steps?: ApprovalFlowStepInput[];
}

export interface InsertApprovalFlowStepsPayload {
  position?: ApprovalStepInsertPosition;
  /** BEFORE/AFTER için zorunlu. */
  referenceStepId?: string;
  steps: ApprovalFlowStepInput[];
}

export interface ApprovalFlowListParams {
  page?: number;
  limit?: number;
  search?: string;
  targetType?: ApprovalTargetType;
  isActive?: boolean;
}

/** Talep içindeki bir DALIN anlık durumu (zaman çizelgesi satırı). */
export interface ApprovalRequestStep {
  id: string;
  /** KADEME numarası. Paralel dallar aynı değeri paylaşır. */
  stepOrder: number;
  /** Kademe içindeki dalın sırası. Tek dallı kademede 0. */
  branchIndex: number;
  parallelPolicy: ApprovalParallelPolicy;
  title: string;
  description: string | null;
  assigneeType: ApprovalAssigneeType;
  /** Dalın rolleri — herhangi biri karar verebilir. */
  roles: ApprovalAssignee[];
  /** @deprecated `roles` kullanın. */
  role: ApprovalAssignee | null;
  user: ApprovalAssignee | null;
  status: ApprovalStepStatus;
  /**
   * Sıra bu dalda mı. Paralel kademede birden fazla dal aynı anda true olabilir;
   * ALL politikasında onaylayan dal false'a düşerken kardeşleri true kalır.
   */
  isCurrent: boolean;
  decidedByName: string | null;
  decidedAt: string | null;
  comment: string | null;
}

export interface ApprovalRequest {
  id: string;
  flowId: string;
  flowTitle: string;
  title: string;
  description: string | null;
  targetType: string;
  targetId: string | null;
  status: ApprovalRequestStatus;
  currentStepOrder: number | null;
  /** Paralel kademede dal başlıkları " / " ile birleştirilir. */
  currentStepTitle: string | null;
  /** KADEME sayısı — paralel dallar tek adım sayılır. */
  totalSteps: number;
  /** Karara bağlanmış KADEME sayısı. */
  decidedSteps: number;
  requesterName: string;
  /** Oturum açan kullanıcı şu anki adımda karar verebiliyor mu — buton bu alana bakar. */
  canDecide: boolean;
  /** Liste ucunda boş dizi döner. */
  steps: ApprovalRequestStep[];
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Hedef kayıt listelerine gömülen talep özeti — enrollment uçlarının `approval`
 * alanı. Bekleyen talep varsa o, yoksa en son sonuçlanan talep.
 */
export interface ApprovalTargetSummary {
  requestId: string;
  status: ApprovalRequestStatus;
  currentStepOrder: number | null;
  currentStepTitle: string | null;
  totalSteps: number;
  /** Oturum açan kullanıcı sıradaki adımda karar verebilir mi. */
  canDecide: boolean;
}

export interface ApprovalDecisionPayload {
  approved: boolean;
  comment?: string;
  /**
   * Kararın yazılacağı DAL (ApprovalRequestStep) ID'si. Yalnızca paralel
   * kademede anlamlıdır; gönderilmezse backend kullanıcının düştüğü ilk bekleyen
   * dalı seçer.
   */
  stepId?: string;
}

export interface ApprovalRequestListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: ApprovalRequestStatus;
  flowId?: string;
  targetType?: ApprovalTargetType;
  targetId?: string;
  mine?: boolean;
  /** Yalnızca sırası oturum açan kullanıcıda olan talepler — "Onay Kutum". */
  awaitingMyDecision?: boolean;
}

/** Aynı kademeyi paylaşan dalların görüntülenebilir grubu. */
export interface ApprovalStepGroup<T extends { stepOrder: number; branchIndex: number }> {
  stepOrder: number;
  branches: T[];
}

/**
 * Düz dal listesini kademelere ayırır: aynı `stepOrder` tek grup, dallar
 * `branchIndex` sırasında.
 *
 * Backend adımları düz gönderiyor (paralel kademe = aynı stepOrder'lı birden
 * fazla satır); her ekran aynı gruplamayı tekrar yazmasın diye burada.
 */
export const groupApprovalSteps = <T extends { stepOrder: number; branchIndex: number }>(
  steps: T[],
): ApprovalStepGroup<T>[] => {
  const byOrder = new Map<number, T[]>();
  for (const step of steps) {
    const bucket = byOrder.get(step.stepOrder);
    if (bucket) bucket.push(step);
    else byOrder.set(step.stepOrder, [step]);
  }
  return [...byOrder.entries()]
    .sort(([a], [b]) => a - b)
    .map(([stepOrder, branches]) => ({
      stepOrder,
      branches: [...branches].sort((a, b) => a.branchIndex - b.branchIndex),
    }));
};
